import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { Controller, useFormContext } from 'react-hook-form';

import { DisplaySymbolMap } from '@core';
import { useTranslate } from '@i18n';
import { swingMeasuredParametersAtom } from '@store';
import { ActionButton, Box, Button, GoalParameterSet, ModalBody, ModalHeader, Spinner, Text, u } from '@ui';

import { LiveAnalysisFormValues } from './schema';
import { useLiveAnalysisSupportedParameters } from './useLiveAnalysisSupportedParameters';
import { useMergedCorridors } from './useMergedCorridors';

const toggleInArray = (values: number[], value: number) =>
    values.includes(value) ? values.filter((v) => v !== value) : [...values, value];

type LiveAnalysisSetupParametersSelectionProps = {
    onClose: () => void;
};

const PARAMETER_CAP = 5;

export const LiveAnalysisSetupParametersSelection = ({ onClose }: LiveAnalysisSetupParametersSelectionProps) => {
    const form = useFormContext<LiveAnalysisFormValues>();

    const supportedParameters = useLiveAnalysisSupportedParameters();
    const corridors = useMergedCorridors();

    const setupParameters = useMemo(() => {
        return supportedParameters?.filter((node) => node.parameter.position?.match(/p1$/));
    }, [supportedParameters]);

    const t = useTranslate();

    const swingMeasuredParameters = useAtomValue(swingMeasuredParametersAtom);
    if (!swingMeasuredParameters?.length) {
        <>
            <ModalHeader
                customButton={
                    <Button rounded onClick={onClose}>
                        Done
                    </Button>
                }
            >
                <Box display="flex" gap={u(3)} alignItems="center">
                    <ActionButton
                        icon="back"
                        variant="secondary"
                        iconSize="medium"
                        buttonSize="small"
                        onClick={onClose}
                    />
                    <Text variant="h3">Select Goal Parameters</Text>
                </Box>
            </ModalHeader>
            <ModalBody py={u(4)} dimmed>
                <Spinner size="l" />
            </ModalBody>
        </>;
    }

    return (
        <>
            <ModalHeader
                customButton={
                    <Button rounded onClick={onClose}>
                        Done
                    </Button>
                }
            >
                <Box display="flex" gap={u(3)} alignItems="center">
                    <ActionButton
                        icon="back"
                        variant="secondary"
                        iconSize="medium"
                        buttonSize="small"
                        onClick={onClose}
                    />
                    <Text variant="h3">Select Goal Parameters</Text>
                </Box>
            </ModalHeader>
            <ModalBody py={u(4)} dimmed>
                <Controller
                    control={form.control}
                    name="setupParameters"
                    rules={{ validate: (value) => value.length > 0 }}
                    render={({ field }) => (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={u(2)}>
                            {!!field.value.length && (
                                <Button onClick={() => field.onChange([])} variant={'secondary'} rounded icon="close">
                                    Clear Selection
                                </Button>
                            )}
                            {setupParameters
                                ?.sort((a, b) => t(a.i18n ?? '-').localeCompare(t(b.i18n ?? '-')))
                                .map((node) => {
                                    const parameterCorridors = corridors?.[node.parameter.smpId];
                                    if (!parameterCorridors) {
                                        return null;
                                    }

                                    return (
                                        <GoalParameterSet
                                            key={node.id}
                                            variant="primary"
                                            title={t(node.i18n ?? '-')}
                                            displaySymbol={DisplaySymbolMap[parameterCorridors.displayUnit]}
                                            readOnly
                                            max={parameterCorridors.max}
                                            min={parameterCorridors.min}
                                            corridor={{
                                                innerMax: parameterCorridors.max,
                                                innerMin: parameterCorridors.min,
                                                unit: parameterCorridors.displayUnit,
                                            }}
                                            isSelected={field.value.includes(node.parameter.smpId)}
                                            onSelect={
                                                field.value.length >= PARAMETER_CAP &&
                                                !field.value.includes(node.parameter.smpId)
                                                    ? undefined
                                                    : () =>
                                                          field.onChange(
                                                              toggleInArray(field.value, node.parameter.smpId),
                                                          )
                                            }
                                        />
                                    );
                                })}
                        </Box>
                    )}
                />
            </ModalBody>
        </>
    );
};

type LiveAnalysisEventParametersSelectionProps = {
    onClose: () => void;
};

export const LiveAnalysisEventParametersSelection = ({ onClose }: LiveAnalysisEventParametersSelectionProps) => {
    const form = useFormContext<LiveAnalysisFormValues>();

    const supportedParameters = useLiveAnalysisSupportedParameters();
    const corridors = useMergedCorridors();

    const eventParameters = useMemo(() => {
        return supportedParameters?.filter((node) => node.parameter.position?.match(/p[246]$/));
    }, [supportedParameters]);

    const t = useTranslate();

    const swingMeasuredParameters = useAtomValue(swingMeasuredParametersAtom);
    if (!swingMeasuredParameters?.length) {
        <>
            <ModalHeader
                customButton={
                    <Button rounded onClick={onClose}>
                        Done
                    </Button>
                }
            >
                <Box display="flex" gap={u(3)} alignItems="center">
                    <ActionButton
                        icon="back"
                        variant="secondary"
                        iconSize="medium"
                        buttonSize="small"
                        onClick={onClose}
                    />
                    <Text variant="h3">Select Goal Parameters</Text>
                </Box>
            </ModalHeader>
            <ModalBody py={u(4)} dimmed>
                <Spinner size="l" />
            </ModalBody>
        </>;
    }

    return (
        <>
            <ModalHeader
                customButton={
                    <Button rounded onClick={onClose}>
                        Done
                    </Button>
                }
            >
                <Box display="flex" gap={u(3)} alignItems="center">
                    <ActionButton
                        icon="back"
                        variant="secondary"
                        iconSize="medium"
                        buttonSize="small"
                        onClick={onClose}
                    />
                    <Text variant="h3">Select Goal Parameters</Text>
                </Box>
            </ModalHeader>
            <ModalBody py={u(4)} dimmed>
                <Controller
                    control={form.control}
                    name="eventParameters"
                    rules={{ required: true }}
                    render={({ field }) => (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={u(2)}>
                            {!!field.value.length && (
                                <Button onClick={() => field.onChange([])} variant={'secondary'} rounded icon="close">
                                    Clear Selection
                                </Button>
                            )}
                            {eventParameters
                                ?.sort((a, b) => t(a.i18n ?? '-').localeCompare(t(b.i18n ?? '-')))
                                ?.map((node) => {
                                    const parameterCorridors = corridors?.[node.parameter.smpId];
                                    if (!parameterCorridors) {
                                        console.error('No corridors found for [nodeId]:', node.id);
                                        return null;
                                    }
                                    return (
                                        <GoalParameterSet
                                            key={node.id}
                                            variant="primary"
                                            title={t(node.i18n ?? '-')}
                                            readOnly
                                            displaySymbol={DisplaySymbolMap[parameterCorridors.displayUnit]}
                                            max={parameterCorridors.max}
                                            min={parameterCorridors.min}
                                            corridor={{
                                                innerMax: parameterCorridors.max,
                                                innerMin: parameterCorridors.min,
                                                unit: parameterCorridors.displayUnit,
                                            }}
                                            isSelected={field.value.includes(node.parameter.smpId)}
                                            onSelect={
                                                field.value.length >= PARAMETER_CAP &&
                                                !field.value.includes(node.parameter.smpId)
                                                    ? undefined
                                                    : () =>
                                                          field.onChange(
                                                              toggleInArray(field.value, node.parameter.smpId),
                                                          )
                                            }
                                        />
                                    );
                                })}
                        </Box>
                    )}
                />
            </ModalBody>
        </>
    );
};
